/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useEffect, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import './FloatingAICompanion.css';

const FloatingAICompanion = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [isChat, setIsChat] = useState(false);
  const [userName, setUserName] = useState('');
  const [hasAskedName, setHasAskedName] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('welcome'); // welcome, contact, thanks
  const [contactForm, setContactForm] = useState({ email: '', phone: '', message: '' });
  const [inputValue, setInputValue] = useState('');
  const [chatHistory, setChatHistory] = useState([]);

  const welcomeMessages = [
    "👋 Hi there! I'm Jerin's AI Assistant",
    "Ready to help you connect with Jerin!",
    "Click to start a conversation 💬",
    "AI-powered networking at your service",
    "Let's explore Jerin's profile together 🚀",
    "Your digital gateway to opportunities"
  ];

  const personalizedMessages = [
    `Great to meet you, ${userName}! 🎉`,
    `How can I help you today, ${userName}?`,
    `${userName}, ready to explore Jerin's work?`,
    `What interests you most, ${userName}?`
  ];

  const currentMessages = hasAskedName && userName ? personalizedMessages : welcomeMessages;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = 80;
    canvas.height = 80;
    
    let particleArray = [];
    
    for (let i = 0; i < 8; i++) {
      particleArray.push({
        angle: (i / 8) * Math.PI * 2,
        radius: 18 + Math.random() * 12,
        speed: 0.015 + Math.random() * 0.01,
        size: Math.random() * 1.8 + 0.3,
        opacity: Math.random() * 0.5 + 0.3,
        color: i % 2 === 0 ? [0, 255, 255] : [100, 200, 255]
      });
    }
    
    const animate = (time) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      const pulse = Math.sin(time * 0.004) * 0.3 + 1;
      
      // Central AI core with gradient
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 10 * pulse);
      gradient.addColorStop(0, `rgba(0, 255, 255, ${0.6 * pulse})`);
      gradient.addColorStop(1, `rgba(0, 255, 255, 0)`);
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, 10 * pulse, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
      
      particleArray.forEach((particle, i) => {
        particle.angle += particle.speed;
        
        const x = centerX + Math.cos(particle.angle) * particle.radius;
        const y = centerY + Math.sin(particle.angle) * particle.radius;
        
        ctx.beginPath();
        ctx.arc(x, y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${particle.color[0]}, ${particle.color[1]}, ${particle.color[2]}, ${particle.opacity})`;
        ctx.fill();
        
        if (i % 3 === 0) {
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(x, y);
          ctx.strokeStyle = `rgba(${particle.color[0]}, ${particle.color[1]}, ${particle.color[2]}, ${particle.opacity * 0.15})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate(0);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isChat) {
      const interval = setInterval(() => {
        setCurrentMessage((prev) => (prev + 1) % currentMessages.length);
      }, 3500);
      
      return () => clearInterval(interval);
    }
  }, [currentMessages.length, isChat]);

  const handleMainClick = () => {
    setIsChat(true);
    setIsExpanded(true);
    setCurrentScreen('welcome');
    if (!hasAskedName && !userName) {
      setChatHistory([{ type: 'bot', message: "👋 Hello! I'm Jerin's AI assistant. What's your name?" }]);
    } else {
      setChatHistory([{ type: 'bot', message: `Hi ${userName}! How can I help you today?` }]);
    }
  };

  const handleSubmitName = () => {
    if (inputValue.trim()) {
      setUserName(inputValue.trim());
      setHasAskedName(true);
      setChatHistory(prev => [...prev, 
        { type: 'user', message: inputValue },
        { type: 'bot', message: `Nice to meet you, ${inputValue.trim()}! 🎉\n\nI can help you with:\n• 📄 View Jerin's Resume\n• 📧 Get Contact Info (Gmail/LinkedIn)\n• 💬 Leave a message for Jerin\n• 🤝 Request a connection\n\nWhat would you like to do?` }
      ]);
      setInputValue('');
    }
  };

  const handleUserMessage = (message) => {
    setChatHistory(prev => [...prev, { type: 'user', message }]);
    
    const lowerMsg = message.toLowerCase();
    let response = '';
    
    if (lowerMsg.includes('resume') || lowerMsg.includes('cv')) {
      response = (
        <div>
          📄 Here's Jerin's resume:
          <br /><br />
          <a 
            href="https://drive.google.com/file/d/1VHZ6s7BLPhcdg1sClMeqdx3B2RFxgWFF/view?usp=sharing" 
            target="_blank" 
            rel="noopener noreferrer"
            className="resume-link"
          >
            🔗 View Resume on Google Drive
          </a>
          <br /><br />
          Feel free to download and review it. Would you like me to help you connect with Jerin?
        </div>
      );
    } else if (lowerMsg.includes('gmail') || lowerMsg.includes('email')) {
      response = `📧 Jerin's Gmail: jerinalour@gmail.com\n\nYou can reach out directly or leave a message through me!`;
    } else if (lowerMsg.includes('linkedin')) {
      response = (
        <div>
          💼 Jerin's LinkedIn:
          <br /><br />
          <a 
            href="https://www.linkedin.com/in/jerinalour/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="linkedin-link"
          >
            🔗 Connect on LinkedIn
          </a>
          <br /><br />
          Great place to connect professionally!
        </div>
      );
    } else if (lowerMsg.includes('contact') || lowerMsg.includes('connect') || lowerMsg.includes('message')) {
      response = `I'd be happy to help you connect with Jerin! Please share your contact details and I'll make sure he gets your message.`;
      setTimeout(() => {
        setChatHistory(prev => [...prev, { type: 'bot', message: response }]);
        setCurrentScreen('contact');
      }, 1000);
      setInputValue('');
      return;
    } else if (lowerMsg.includes('hello') || lowerMsg.includes('hi')) {
      response = `Hello ${userName}! 👋 I'm here to help you connect with Jerin. What can I do for you?`;
    } else {
      response = `I can help you with:\n• 📄 Resume - "show resume"\n• 📧 Email - "gmail" or "email"\n• 💼 LinkedIn - "linkedin"\n• 💬 Contact - "connect" or "message"\n\nWhat would you like to know?`;
    }
    
    setTimeout(() => {
      setChatHistory(prev => [...prev, { type: 'bot', message: response }]);
    }, 1000);
    
    setInputValue('');
  };

  const handleContactSubmit = async () => {
    if (contactForm.email || contactForm.phone) {
      try {
        // Check if EmailJS environment variables are available
        const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
        
        // Debug logging (remove in production)
        console.log('EmailJS Config Check:', {
          serviceId: serviceId ? 'Set' : 'Missing',
          templateId: templateId ? 'Set' : 'Missing',
          publicKey: publicKey ? 'Set' : 'Missing'
        });
        
        if (!serviceId || !templateId || !publicKey) {
          throw new Error('EmailJS configuration is missing. Please check your environment variables.');
        }
        
        // Initialize EmailJS (if not already done)
        emailjs.init(publicKey);
        
        // Send email via EmailJS
        const result = await emailjs.send(
          serviceId,
          templateId,
          {
            user_name: userName,
            user_email: contactForm.email,
            user_phone: contactForm.phone || 'Not provided',
            message: contactForm.message || 'No message provided',
            timestamp: new Date().toLocaleString(),
            to_name: 'Jerin',
            from_name: userName
          },
          publicKey
        );
        
        console.log('Email sent successfully:', result);
        
        setChatHistory(prev => [...prev, 
          { type: 'user', message: `Contact: ${contactForm.email || contactForm.phone}${contactForm.message ? `\nMessage: ${contactForm.message}` : ''}` },
          { type: 'bot', message: `✅ Perfect! Your message has been sent to Jerin:\n\n${contactForm.email ? `📧 ${contactForm.email}` : ''}\n${contactForm.phone ? `📱 ${contactForm.phone}` : ''}\n\nJerin will reach out to you soon. Thank you for connecting! 🙏` }
        ]);
        
        setContactForm({ email: '', phone: '', message: '' });
        setCurrentScreen('welcome');
        
      } catch (error) {
        console.error('Failed to send email:', error);
        
        // Provide more specific error messages
        let errorMessage = '';
        if (error.message.includes('configuration is missing')) {
          errorMessage = `❌ EmailJS is not properly configured. Please contact the site administrator.\n\nIn the meantime, you can email Jerin directly at: jerinalour@gmail.com`;
        } else if (error.status === 422) {
          errorMessage = `❌ Please check your email address format and try again.`;
        } else if (error.status === 400) {
          errorMessage = `❌ There was an issue with the message format. Please try again.`;
        } else {
          errorMessage = `❌ Sorry, there was an issue sending your message. Please try again or email Jerin directly at jerinalour@gmail.com`;
        }
        
        setChatHistory(prev => [...prev, 
          { type: 'bot', message: errorMessage }
        ]);
        
        // Don't clear the form on error so user can try again
      }
    }
  };

  const handleBackToChat = () => {
    setCurrentScreen('welcome');
  };

  const renderChatContent = () => {
    if (!isChat) {
      return (
        <div className="welcome-content">
          <div className="welcome-message">
            {currentMessages[currentMessage]}
          </div>
          <div className="welcome-subtext">
            Click the AI assistant to start
          </div>
        </div>
      );
    }

    return (
      <div className="chat-container">
        {/* Header */}
        <div className="chat-header">
          <div className="status-indicator"></div>
          <span className="assistant-title">Jerin's AI Assistant</span>
          <button 
            className="close-button"
            onClick={() => { 
              setIsChat(false); 
              setIsExpanded(false); 
              setCurrentScreen('welcome');
              setChatHistory([]);
            }}
          >×</button>
        </div>

        {/* Chat Messages or Contact Form */}
        {currentScreen === 'contact' ? (
          <div className="contact-form-container">
            <div className="contact-title">
              📧 Contact Information
            </div>
            <div className="contact-subtitle">
              Leave your contact info and I'll make sure Jerin gets your message!
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Email Address *
              </label>
              <input
                type="email"
                placeholder="your.email@example.com"
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={contactForm.phone}
                onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Message (Optional)
              </label>
              <textarea
                placeholder="Tell Jerin what you'd like to discuss..."
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                className="form-textarea"
              />
            </div>
            
            <div className="form-buttons">
              <button
                onClick={handleBackToChat}
                className="back-button"
              >
                ← Back
              </button>
              <button
                onClick={handleContactSubmit}
                disabled={!contactForm.email}
                className={`submit-button ${!contactForm.email ? 'disabled' : ''}`}
              >
                Send Contact Info
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Messages */}
            <div className="chat-messages">
              {chatHistory.map((chat, index) => (
                <div key={index} className={`message-wrapper ${chat.type}`}>
                  <div className="message-bubble">
                    {typeof chat.message === 'string' ? chat.message : chat.message}
                  </div>
                </div>
              ))}
            </div>

            {/* Input Area */}
            <div className="chat-input-area">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    if (!hasAskedName) {
                      handleSubmitName();
                    } else {
                      handleUserMessage(inputValue);
                    }
                  }
                }}
                placeholder={!hasAskedName ? "Enter your name..." : "Type your message..."}
                className="message-input"
              />
              <button
                onClick={!hasAskedName ? handleSubmitName : () => handleUserMessage(inputValue)}
                className="send-button"
              >
                Send
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="floating-container">
      {/* Message bubble */}
      <div className={`message-bubble-container ${isExpanded ? 'expanded' : ''}`}>
        {renderChatContent()}
        
        {/* Speech bubble tail */}
        <div className="speech-tail"></div>
      </div>

      {/* Main floating widget */}
      <div 
        className={`main-widget ${isExpanded ? 'expanded' : ''}`}
        onClick={handleMainClick}
        onMouseEnter={() => !isChat && setIsExpanded(true)}
        onMouseLeave={() => !isChat && setIsExpanded(false)}
      >
        <canvas 
          ref={canvasRef}
          className="widget-canvas"
        />
        
        {/* Robot head */}
        <div className="robot-head">
          <span role="img" aria-label="AI Robot">🤖</span>
        </div>
        
        <div className="status-dot"></div>
      </div>
    </div>
  );
};

export default FloatingAICompanion;
